import {
  ACTION,
  Dialog,
  Divider,
  OrderStatus,
  Text,
  VStack,
  abbreviateId,
  useDynamicCallback,
  useSecuritiesContext,
  type DialogProps,
  type Order,
} from '@talos/kyoko';

import { OMSView } from '@src/components/OMS/OMSView';
import { useRoleAuth } from '@src/hooks';
import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import { orderSliceActions } from '../../../../components/OMS/NewOrder/OrderSlice';
import { getOrderFormEligibilityForSymbol } from '../../../../components/OMS/NewOrder/orderFormSecurityEligibility';
import { openView } from '../../../../components/OMS/OMSSlice';
import { SelectedOrdersList } from './SelectedOrdersList';
import { SelectedOrdersUsers } from './SelectedOrdersUsers';
import { CancelDialogWrapper, Row } from './styles';

interface BulkModifyWarningDialogProps extends DialogProps {
  selectedOrders: Order[];
}

export const BulkModifyWarningDialog = (props: BulkModifyWarningDialogProps) => {
  if (!props.selectedOrders.length) {
    return null;
  }
  return ModifyWarningDialog(props);
};

export const ModifyWarningDialog = (props: BulkModifyWarningDialogProps) => {
  return props.selectedOrders.length === 1 ? (
    <SingleOrderModifyWarningDialog {...props} />
  ) : (
    <MultipleOrdersModifyWarningDialog {...props} />
  );
};

const SingleOrderModifyWarningDialog = ({ selectedOrders, ...props }: BulkModifyWarningDialogProps) => {
  const dispatch = useAppStateDispatch();
  const { isAuthorized } = useRoleAuth();
  const { securitiesBySymbol } = useSecuritiesContext();

  const selectedOrder = selectedOrders[0];
  const formEligibility = getOrderFormEligibilityForSymbol(selectedOrder?.Symbol, securitiesBySymbol);

  const handleConfirm = useDynamicCallback(() => {
    if (!formEligibility.allowed) {
      return;
    }
    dispatch(openView(OMSView.NewOrderForm));
    dispatch(orderSliceActions.modifyOrder(selectedOrder));
  });

  return (
    <Dialog
      {...props}
      onConfirm={handleConfirm}
      confirmLabel="Proceed"
      cancelLabel="Return"
      data-testid="modify-warning-dialog"
      confirmDisabled={!isAuthorized(ACTION.SUBMIT_ORDER) || !formEligibility.allowed}
      title="Confirmation"
    >
      <CancelDialogWrapper>
        <Row>
          <h3>
            Order #<b>{abbreviateId(selectedOrder?.OrderID)}</b>
          </h3>
          <OrderStatus
            ordStatus={selectedOrder?.OrdStatus}
            decisionStatus={selectedOrder?.DecisionStatus}
            cumQty={selectedOrder?.CumQty}
            orderQty={selectedOrder?.OrderQty}
            pricingMode={selectedOrder?.PricingMode}
            tooltipText={selectedOrder?.Text}
            isCustomerOrder={false}
          />
        </Row>
        {formEligibility.reason != null ? (
          <p>{formEligibility.reason}</p>
        ) : (
          <p>
            Are you sure you want to modify an order belonging to <b>{selectedOrder?.User}</b>?
          </p>
        )}
      </CancelDialogWrapper>
    </Dialog>
  );
};

const MultipleOrdersModifyWarningDialog = ({ selectedOrders, ...props }: BulkModifyWarningDialogProps) => {
  const { isAuthorized } = useRoleAuth();
  return (
    <Dialog
      {...props}
      data-testid="modify-warning-dialog"
      confirmLabel="Proceed"
      cancelLabel="Return"
      confirmDisabled={!isAuthorized(ACTION.SUBMIT_ORDER)}
      title="Confirmation"
    >
      <CancelDialogWrapper>
        <VStack gap="spacing8" alignItems="stretch">
          <Text style={{ textAlign: 'left' }} color="colorTextAttention">
            You are about to modify {selectedOrders.length} orders belonging to{' '}
            <SelectedOrdersUsers selectedOrders={selectedOrders} />
            .<br />
            Are you sure you want to proceed?
          </Text>
          <Divider />
          <SelectedOrdersList selectedOrders={selectedOrders} />
        </VStack>
      </CancelDialogWrapper>
    </Dialog>
  );
};
